/** Minimum Connector build that implements local_library_remove_all (SQLite + sources). */
export const MIN_REMOVE_ALL_CONNECTOR = '11.0.31';

/** Minimum Connector build with unified catalog_stats source summary + reset_catalog IPC. */
export const MIN_UNIFIED_CATALOG_CONNECTOR = '11.0.70';

/** Minimum Connector build that emits maestro://update-* events from manual check (not sync string). */
export const MIN_EVENT_BASED_UPDATE_CHECK = '11.0.221';

/** Re-exported from connectorCapability — minimum for any supported Connector session. */
export { MIN_SUPPORTED_CONNECTOR, MIN_BIT_PERFECT_CONNECTOR } from './connectorCapability';

export type ParsedConnectorVersion = {
  major: number;
  minor: number;
  patch: number;
  raw: string;
};

export function getConnectorVersion(): string {
  if (typeof window === 'undefined') return '';
  const w = window as Window & { __MAESTRO_CONNECTOR__?: { version?: string } };
  return w.__MAESTRO_CONNECTOR__?.version || '';
}

export function parseConnectorVersion(v: string | null | undefined): ParsedConnectorVersion | null {
  if (!v) return null;
  const stripped = v.replace(/^v/i, '').trim();
  const m = stripped.match(/^(\d+)\.(\d+)\.(\d+)/);
  if (!m) return null;
  const major = Number(m[1]);
  const minor = Number(m[2]);
  const patch = Number(m[3]);
  if ([major, minor, patch].some((n) => Number.isNaN(n))) return null;
  return { major, minor, patch, raw: stripped };
}

export function connectorMeetsMinVersion(
  current: string | null | undefined,
  minVersion: string,
): boolean {
  const cur = parseConnectorVersion(current);
  const min = parseConnectorVersion(minVersion);
  if (!cur || !min) return false;
  if (cur.major !== min.major) return cur.major > min.major;
  if (cur.minor !== min.minor) return cur.minor > min.minor;
  return cur.patch >= min.patch;
}

/** True when current <= maxVersion (same major.minor.patch semver rules as meets-min). */
export function connectorWithinMaxVersion(
  current: string | null | undefined,
  maxVersion: string,
): boolean {
  const cur = parseConnectorVersion(current);
  const max = parseConnectorVersion(maxVersion);
  if (!cur || !max) return false;
  if (cur.major !== max.major) return cur.major < max.major;
  if (cur.minor !== max.minor) return cur.minor < max.minor;
  return cur.patch <= max.patch;
}

/** Windows WASAPI arbiter crash window — suppress web exclusive dispatch until v164. */
export function isConnectorInWindowsExclusiveKillRange(): boolean {
  const v = getConnectorVersion();
  return connectorMeetsMinVersion(v, '11.0.160')
    && connectorWithinMaxVersion(v, '11.0.163');
}

export function connectorSupportsLocalRemoveAll(): boolean {
  return connectorMeetsMinVersion(getConnectorVersion(), MIN_REMOVE_ALL_CONNECTOR);
}

export function connectorSupportsLocalSourceSummary(): boolean {
  return connectorMeetsMinVersion(getConnectorVersion(), MIN_UNIFIED_CATALOG_CONNECTOR);
}

export function connectorSupportsResetCatalog(): boolean {
  return connectorMeetsMinVersion(getConnectorVersion(), MIN_UNIFIED_CATALOG_CONNECTOR);
}

export function connectorSupportsEventBasedUpdateCheck(): boolean {
  return connectorMeetsMinVersion(getConnectorVersion(), MIN_EVENT_BASED_UPDATE_CHECK);
}

export function removeAllRequiresConnectorMessage(): string {
  const installed = getConnectorVersion();
  const label = installed ? `v${installed.replace(/^v/i, '')}` : 'unknown';
  return `Remove All requires Maestro Connector v${MIN_REMOVE_ALL_CONNECTOR} or newer (installed: ${label}). Update the Connector, then try again.`;
}
